import { useDifficulties } from "~/hooks/useDifficulties"
import { difficultyLabel } from "~/lib/difficulty"
import { EntityIdentity, UnassignedIdentity } from "~/components/EntityIdentity"
import { useDraggable } from "@dnd-kit/core"
import { CalendarIcon } from "lucide-react"
import { Link } from "~/components/Link"
import { usePermission } from "~/hooks/usePermission"
import { TeamPermissions } from "~/lib/permissions"
import { cn, enumFormat, formatDate, getEditorPlainText } from "~/lib/utils"
import type { RouterOutputs } from "~/trpc/react"
import { DifficultyIcon } from "../icons/difficulty"
import { PriorityIcon } from "../icons/priority"
import { StatusIcon } from "../icons/status"
import { Marquee } from "~/components/ui/marquee"
import { IssueLabelShowcase } from "../issueDetailsPage/IssueLabelShowcase"
import { Card, CardContent, CardFooter, CardHeader } from "../ui/card"
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger
} from "../ui/tooltip"

export function IssueCard({
	issue,
	isPending = false,
	onOpen
}: {
	issue: RouterOutputs["issues"]["getTeamIssues"]["issues"][number]
	isPending?: boolean
	onOpen?: () => void
}) {
	const difficulties = useDifficulties(issue.teamId)
	const { hasTeamPermission, teamLoaded } = usePermission()
	const canEdit = hasTeamPermission(TeamPermissions.EDIT_ISSUES)
	const labels = issue.labels || []
	const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
		id: issue.id,
		data: issue,
		disabled: !canEdit || !teamLoaded
	})

	const description = getEditorPlainText(issue.description)
	const truncatedDescription = description
		? description.slice(0, 100) + (description.length > 100 ? "..." : "")
		: null

	return (
		<div ref={setNodeRef} className="relative">
			<div
				{...attributes}
				{...listeners}
				className="absolute inset-0 cursor-grab active:cursor-grabbing"
			/>
			<TooltipProvider>
				<Card
					className={cn(
						"hover:border-stroke-hover transition-colors py-3 relative z-10 pointer-events-none",
						isDragging && "opacity-50",
						isPending && "opacity-50 pointer-events-none select-none"
					)}
				>
					<CardHeader className="px-3 gap-2">
						<div className="flex items-center justify-between pointer-events-none">
							<span className="text-sm text-text-small font-medium">
								{issue.team.name.toUpperCase()}-{issue.friendlyId}
							</span>
							<Tooltip>
								<TooltipTrigger asChild>
									<PriorityIcon priority={issue.priority} />
								</TooltipTrigger>
								<TooltipContent>
									Priority: {enumFormat(issue.priority)}
								</TooltipContent>
							</Tooltip>
						</div>
						<Link
							to={`/${issue.team.organization.slug}/${issue.team.slug}/${onOpen ? `queue?issue=${issue.friendlyId}` : issue.friendlyId}`}
							onClick={(event) => {
								if (
									onOpen &&
									event.button === 0 &&
									!event.metaKey &&
									!event.ctrlKey &&
									!event.shiftKey &&
									!event.altKey
								) {
									event.preventDefault()
									onOpen()
								}
							}}
							className="min-w-0 text-sm font-medium text-text-main pointer-events-auto"
						>
							<Marquee>{issue.title}</Marquee>
						</Link>
					</CardHeader>

					<CardContent className="px-3 flex flex-col gap-2">
						{truncatedDescription && (
							<p className="text-sm text-text-small whitespace-pre-wrap">
								{truncatedDescription}
							</p>
						)}
						<div className="pointer-events-auto">
							<IssueLabelShowcase labels={labels} />
						</div>
						{issue.dueDate && (
							<Tooltip>
								<TooltipTrigger asChild>
									<span className="flex items-center gap-1 text-xs text-text-small">
										<CalendarIcon className="size-3.5" />
										Due {formatDate(issue.dueDate)}
									</span>
								</TooltipTrigger>
								<TooltipContent>Due date</TooltipContent>
							</Tooltip>
						)}
					</CardContent>

					<CardFooter className="px-3 justify-between items-center">
						<div className="flex items-center gap-1.5 text-text-small">
							<Tooltip>
								<TooltipTrigger asChild>
									<DifficultyIcon
										difficulty={issue.difficulty}
										teamId={issue.teamId}
										className="size-4"
									/>
								</TooltipTrigger>
								<TooltipContent>
									Difficulty: {difficultyLabel(issue.difficulty, difficulties)}
								</TooltipContent>
							</Tooltip>
							<Tooltip>
								<TooltipTrigger asChild>
									<StatusIcon status={issue.status} className="size-4" />
								</TooltipTrigger>
								<TooltipContent>
									Status: {enumFormat(issue.status)}
								</TooltipContent>
							</Tooltip>
						</div>
						{issue.assignee ? (
							<Tooltip>
								<TooltipTrigger asChild>
									<EntityIdentity
										kind="user"
										variant="avatar"
										size="sm"
										name={issue.assignee.orgMember.user?.name ?? "?"}
										image={issue.assignee.orgMember.user?.image}
										lastSeenAt={issue.assignee.orgMember.user?.lastSeenAt}
									/>
								</TooltipTrigger>
								<TooltipContent>
									Assignee: {issue.assignee?.orgMember.user?.name}
								</TooltipContent>
							</Tooltip>
						) : (
							<UnassignedIdentity size="md" />
						)}
					</CardFooter>
				</Card>
			</TooltipProvider>
		</div>
	)
}
